import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { EdgeDevice, EdgeDeviceCollision } from '../../core/models';
import { deviceStatusLabel, deviceStatusSeverity, deviceStatusTitle, livenessLabel, livenessSeverity, relativeTime } from './store-edge-status';

type Decision = 'approve' | 'reject' | 'forget';

// MACHINES THAT LET THEMSELVES IN. A store PC is installed unattended, enrols itself and reaches nothing until
// somebody here approves it — so this card is where that decision is made, and it carries what makes the
// decision informed: the machine's name, the store it claims, whether that store is onboarded, where it called
// from, and how long it has been waiting.
//
// The list is NOT re-sorted here. The API returns it undecided-first and oldest-first inside that, which is the
// order the rows need attention in.
//
// It does NOT own a timer: the Store Edges page drives both its cards from one interval, so the two never
// refresh out of step. `reload()` is what that tick calls.
@Component({
    selector: 'app-edge-devices',
    standalone: true,
    imports: [FormsModule, RouterLink, DatePipe, CardModule, ButtonModule, TableModule, TagModule, MessageModule, SelectModule, DialogModule, TooltipModule],
    template: `
        <p-card styleClass="mb-4">
            <ng-template pTemplate="title">
                <span>Edge devices</span>
                <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help"
                   pTooltip="Machines that enrolled themselves. The merchant filter does not narrow this list: a device has no merchant until it is approved and bound to a store."
                   tooltipPosition="top"></i>
            </ng-template>

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="filters" optionLabel="label" optionValue="value"
                          [ngModel]="status()" (ngModelChange)="onStatusChange($event)"
                          styleClass="w-full sm:w-56" />
                <p-button icon="pi pi-refresh" [text]="true" size="small" [loading]="refreshing()" (onClick)="refresh()" />
                @if (waiting() > 0) {
                    <p-tag severity="warn" [value]="waiting() === 1 ? '1 waiting' : waiting() + ' waiting'" />
                }
            </div>

            @if (error()) {
                <p-message severity="error" styleClass="mb-3">{{ error() }}</p-message>
            }
            @if (note()) {
                <p-message severity="success" styleClass="mb-3">{{ note() }}</p-message>
            }

            <p-table [value]="devices()" [loading]="loading()" [paginator]="devices().length > 10" [rows]="10">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width: 7rem">Status</th>
                        <th>Machine</th>
                        <th>Claimed location</th>
                        <th>Store</th>
                        <th>Reported</th>
                        <th>First asked</th>
                        <th>Decided</th>
                        <th style="width: 11rem"></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-d>
                    <tr>
                        <td>
                            <p-tag [value]="decisionLabel(d)" [severity]="decisionSeverity(d)" [attr.title]="decisionTitle(d)" />
                        </td>
                        <td class="font-medium" [attr.title]="'Device id ' + d.deviceId">
                            {{ d.machineName }}
                            <!-- Something is already registered as that name at that store, so approving this
                                 machine would re-key it. Said on the row rather than only in the dialog: it is
                                 why this device will not bind, and an operator scanning the queue should see it
                                 without clicking. -->
                            @if (d.collision) {
                                <p-tag severity="warn" value="Name taken" styleClass="ml-2"
                                       [attr.title]="collisionTitle(d.collision)" />
                            }
                            <!-- Where it called from, which installation it is, and which build - the three things
                                 an operator matches against the PC in front of them (or the technician on the
                                 phone). The installation id is this install's own name for itself; a reimaged
                                 PC comes back with a new one, which is how it shows up as a new device. -->
                            <div class="text-xs text-surface-500">
                                {{ d.requestAddress ?? 'address not recorded' }}
                                <span class="mx-1">·</span>
                                <span class="font-mono" [attr.title]="'Installation ' + d.deviceId">{{ shortDeviceId(d.deviceId) }}</span>
                                @if (d.lastSeenVersion) {
                                    <span class="mx-1">·</span>
                                    <span class="font-mono" [attr.title]="'Build ' + d.lastSeenVersion">{{ d.lastSeenVersion }}</span>
                                }
                            </div>
                        </td>
                        <!-- The claim and the truth are two facts, and the cell keeps them apart: a link only when
                             the platform actually has that location, plain text plus a badge when it does not. -->
                        <td>
                            @if (d.locationOnboarded) {
                                <a class="text-primary hover:underline" [routerLink]="['/locations', d.claimedLocationId]">
                                    {{ d.claimedLocationName ?? d.claimedLocationId }}
                                </a>
                            } @else {
                                <span class="font-mono text-sm">{{ d.claimedLocationId }}</span>
                                <p-tag severity="secondary" value="Not onboarded" styleClass="ml-2"
                                       [attr.title]="'No location with this id exists yet. The device can be approved now and binds when the store is onboarded.'" />
                            }
                        </td>
                        <td class="text-sm">
                            @if (d.bound) {
                                <span class="whitespace-nowrap">Bound</span>
                            } @else if (d.status === 'approved' && d.bindingProblem) {
                                <!-- The store is there and the machine is approved, and still they are not linked:
                                     the reason is the only thing worth the space here, because "Not bound" over
                                     an onboarded store reads as "wait", and waiting is the one thing that will
                                     not fix it. -->
                                <span class="whitespace-nowrap text-orange-600">Not bound</span>
                                <div class="text-xs text-surface-600 mt-1 max-w-[28rem] break-words"
                                     [attr.title]="d.bindingProblem">{{ d.bindingProblem }}</div>
                            } @else if (d.status === 'approved') {
                                <span class="text-surface-500 whitespace-nowrap">Not bound</span>
                            } @else {
                                <span class="text-surface-500">—</span>
                            }
                        </td>
                        <!-- Liveness, and only once there is something to be live: a device with no key has never
                             had a way to report in, so a status badge there would read as a fault. -->
                        <td class="text-sm whitespace-nowrap"
                            [attr.title]="d.lastSeenAt ? (d.lastSeenAt | date: 'medium') : 'Never heard from'">
                            @if (d.keyIssuedAt) {
                                <p-tag [value]="livenessLabel(d.liveness)" [severity]="livenessSeverity(d.liveness)" />
                            } @else if (d.status === 'pending' && stoppedAsking(d)) {
                                <!-- A waiting machine asks every few seconds. One that has gone quiet is off, or was
                                     reinstalled and is now the row above under a new id - either way, not a row to
                                     approve, and the one thing worth saying about it. -->
                                <span class="text-orange-600"
                                      title="This machine has not asked since then. A PC that was reinstalled or reimaged enrols under a new id and leaves its old request here; remove it.">
                                    Stopped asking {{ since(d.lastRequestedAt) }}
                                </span>
                            } @else if (d.status === 'pending') {
                                <span class="text-surface-500" [attr.title]="'Last asked ' + since(d.lastRequestedAt)">Asking</span>
                            } @else {
                                <span class="text-surface-500">No key yet</span>
                            }
                        </td>
                        <!-- WHEN IT FIRST ASKED, not when it last did. A waiting machine polls every few seconds,
                             so last contact would read "less than a minute ago" on every row for ever and the
                             operator could not tell a PC installed this morning from one that has been waiting a
                             week — which is also the order these rows are in. Last contact is the hover. -->
                        <td class="text-sm whitespace-nowrap" [attr.title]="'Last asked ' + (d.lastRequestedAt | date: 'medium')">
                            {{ since(d.firstSeenAt) }}
                        </td>
                        <td class="text-sm whitespace-nowrap" [attr.title]="d.decisionNote ?? null">
                            @if (d.decidedAt) {
                                {{ d.decidedBy }} · {{ since(d.decidedAt) }}
                            } @else {
                                <span class="text-surface-500">—</span>
                            }
                        </td>
                        <td>
                            <div class="flex justify-end gap-1">
                                @if (d.status === 'pending') {
                                    <p-button label="Approve" size="small" [text]="true" (onClick)="open('approve', d)" />
                                    <p-button label="Reject" size="small" [text]="true" severity="danger" (onClick)="open('reject', d)" />
                                } @else {
                                    <!-- THE NAME WAS TAKEN AFTER THIS DEVICE WAS APPROVED — by a hand
                                         registration at that store, or by another machine binding first. It is
                                         approved, alive and holding its key, and it will never bind, because
                                         consent to displace an Edge is recorded by the approve route and
                                         nowhere else. So the row that shows the collision has to offer it:
                                         without this the operator's only move here is Forget, which throws
                                         away the key this machine already holds and makes it enrol again. -->
                                    @if (d.status === 'approved' && !d.bound && d.collision) {
                                        <p-button label="Replace" size="small" [text]="true" (onClick)="open('approve', d)" />
                                    }
                                    <p-button icon="pi pi-trash" [text]="true" size="small" severity="secondary"
                                              ariaLabel="Forget this request" pTooltip="Forget"
                                              (onClick)="open('forget', d)" />
                                }
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="8">
                            {{ error()
                                ? 'The devices could not be read — the banner above says why.'
                                : 'No machine has enrolled itself yet.' }}
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>

        <p-dialog [header]="dialogHeader()" [(visible)]="dialogOpen" [modal]="true"
                  [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '95vw' }">
            @if (target(); as d) {
                <p class="text-sm mb-3">
                    @switch (decision()) {
                        @case ('approve') {
                            @if (takingOver()) {
                                <b>{{ d.machineName }}</b> was approved already and holds its key — what it does
                                not have is a store, because the name it calls itself by is taken at
                                <b>{{ d.claimedLocationId }}</b>. This is the decision that hands it that Edge.
                            } @else {
                                Approve <b>{{ d.machineName }}</b> for <b>{{ d.claimedLocationId }}</b>? It gets a
                                key on its next contact and can report in.
                                @if (!d.locationOnboarded) {
                                    No location with that id exists yet, so no orders reach it until the store is
                                    onboarded and linked.
                                }
                            }
                        }
                        @case ('reject') {
                            Reject <b>{{ d.machineName }}</b>? It is told to stop asking and cannot return to this
                            list by calling again.
                        }
                        @case ('forget') {
                            Remove <b>{{ d.machineName }}</b>?
                            @if (d.bound) {
                                It is serving its store now: its key stops working at once and its orders stop
                                until it is approved again. It enrols again on its own within a minute and
                                reappears here.
                            } @else {
                                It enrols again on its own and reappears here for approval.
                            }
                        }
                    }
                </p>
                <!-- THE REPLACEMENT, NAMED. An Edge is identified by (location, machine name), so approving a
                     machine whose name is already taken at that store re-keys the incumbent — and the replaced
                     key stops working at once, which on a trading till is an outage nobody asked for. So the
                     machine being replaced is named here, with who registered it and whether it is up right now,
                     and the button below says "Replace and approve" rather than "Approve". -->
                @if (decision() === 'approve') {
                    @if (collision(); as c) {
                    <p-message severity="warn" styleClass="mb-3">
                        <span class="text-sm">
                            An Edge called <b>{{ c.machineName }}</b> is already registered at
                            <b>{{ c.locationId }}</b> — registered by {{ c.registeredBy }}
                            {{ since(c.registeredAt) }}, {{ livenessLabel(c.liveness).toLowerCase() }}.
                            Confirming replaces that machine with <b>{{ d.machineName }}</b>: its key stops
                            working immediately and it cannot report in again until somebody reinstalls it.
                            If both tills are real, rename this one instead and approve it after.
                        </span>
                    </p-message>
                    }
                }
                @if (decision() !== 'forget') {
                    <textarea rows="2" [ngModel]="decisionNote()" (ngModelChange)="decisionNote.set($event)"
                              class="w-full text-sm p-2 border border-surface-300 dark:border-surface-700 rounded bg-surface-0 dark:bg-surface-900"
                              placeholder="Note (optional)"></textarea>
                }
            }
            @if (dialogError()) { <p-message severity="error">{{ dialogError() }}</p-message> }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="busy()" (onClick)="dialogOpen.set(false)" />
                <p-button [label]="confirmLabel()" [loading]="busy()"
                          [severity]="decision() === 'approve' ? 'primary' : 'danger'" (onClick)="confirm()" />
            </ng-template>
        </p-dialog>
    `
})
export class EdgeDevices implements OnInit, OnDestroy {
    private readonly api = inject(MiddlewareApi);

    readonly devices = signal<EdgeDevice[]>([]);
    // True only before the first answer: a tick that greyed the table every 20 seconds would make a page nobody
    // touched look busy.
    readonly loading = signal(true);
    readonly refreshing = signal(false);
    readonly error = signal<string | null>(null);
    readonly note = signal<string | null>(null);
    readonly status = signal<EdgeDevice['status'] | null>(null);
    /** The installation id as it fits on a row: "dev-7f1f3c52…3b8b5a1d0c11". The whole id is the hover. */
    shortDeviceId(id: string): string {
        return id.length > 24 ? `${id.slice(0, 12)}…${id.slice(-12)}` : id;
    }

    /** What the operator types in the dialog, kept apart from `note` above, which is the success banner. */
    readonly decisionNote = signal<string | null>(null);

    readonly dialogOpen = signal(false);
    readonly decision = signal<Decision>('approve');
    readonly target = signal<EdgeDevice | null>(null);
    readonly dialogError = signal<string | null>(null);
    readonly busy = signal(false);

    /** The Edge this approval would replace. Seeded from the row and re-seeded from a 409, because a collision
     *  can appear between the list being read and the button being pressed — a machine registered at that store
     *  in the meantime — and the operator has to see THAT one before confirming. */
    readonly collision = signal<EdgeDeviceCollision | null>(null);

    private loadSub?: Subscription;
    private actionSub?: Subscription;

    readonly filters = [
        { label: 'All devices', value: null },
        { label: 'Waiting', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' }
    ];

    readonly decisionLabel = deviceStatusLabel;
    readonly decisionSeverity = deviceStatusSeverity;
    readonly decisionTitle = deviceStatusTitle;
    readonly livenessLabel = livenessLabel;
    readonly livenessSeverity = livenessSeverity;
    readonly since = relativeTime;

    /** A pending machine that has not asked for two minutes: it polls every few seconds, so that is a machine
     *  that is off, or one that was reinstalled and is now in the list under a new id. */
    stoppedAsking(d: EdgeDevice): boolean {
        const last = Date.parse(d.lastRequestedAt);
        return Number.isFinite(last) && Date.now() - last > 2 * 60_000;
    }

    // The first read happens here rather than on the page's tick, so the card is filled the moment it paints
    // instead of a refresh interval later. Every read after this one comes from the page's single timer.
    ngOnInit(): void {
        this.reload();
    }

    ngOnDestroy(): void {
        this.loadSub?.unsubscribe();
        this.actionSub?.unsubscribe();
    }

    /** The button beside the filter. It also clears the banner from the last decision, which the tick must not:
     *  a confirmation that disappeared twenty seconds after it was read would leave the operator unsure whether
     *  the click landed. The same split as the Edges card below. */
    refresh(): void {
        this.note.set(null);
        this.reload();
    }

    /** Called by the page's own interval, so both cards move on the same tick. */
    reload(): void {
        // A tick that lands while the previous one is still in flight replaces it, so a slow middleware cannot
        // leave requests piling up behind it.
        this.loadSub?.unsubscribe();
        this.refreshing.set(true);
        this.loadSub = this.api.getEdgeDevices(this.status()).subscribe({
            next: (devices) => {
                this.devices.set(devices ?? []);
                this.error.set(null);
                this.loading.set(false);
                this.refreshing.set(false);
            },
            // The rows stay: a refresh that failed says nothing about the machines, only about this request.
            error: (e) => {
                this.error.set(this.describe(e, 'merchants:read'));
                this.loading.set(false);
                this.refreshing.set(false);
            }
        });
    }

    waiting(): number {
        return this.devices().filter((d) => d.status === 'pending').length;
    }

    onStatusChange(status: EdgeDevice['status'] | null): void {
        this.status.set(status);
        // A different question, so the old answer is not kept on screen under the new heading.
        this.devices.set([]);
        this.loading.set(true);
        this.reload();
    }

    open(decision: Decision, device: EdgeDevice): void {
        this.decision.set(decision);
        this.target.set(device);
        this.decisionNote.set(null);
        this.dialogError.set(null);
        this.collision.set(decision === 'approve' ? device.collision : null);
        this.dialogOpen.set(true);
    }

    /** What the row's badge says on hover — the same facts as the dialog, in one line. */
    collisionTitle(collision: EdgeDeviceCollision): string {
        return `An Edge called ${collision.machineName} is already registered at ${collision.locationId}`
            + ` by ${collision.registeredBy}. Approving this machine would replace it.`;
    }

    /** The device was let in long ago and the only thing missing is consent to displace the Edge its name
     *  collides with — so the words are about the replacement rather than about letting a machine in. */
    takingOver(): boolean {
        return this.target()?.status === 'approved';
    }

    dialogHeader(): string {
        switch (this.decision()) {
            case 'approve': return this.takingOver() ? 'Give this device that Edge' : 'Approve this device';
            case 'reject': return 'Reject this device';
            case 'forget': return 'Forget this request';
        }
    }

    confirmLabel(): string {
        switch (this.decision()) {
            case 'approve':
                if (!this.collision()) return 'Approve';
                return this.takingOver() ? 'Replace that Edge' : 'Replace and approve';
            case 'reject': return 'Reject';
            case 'forget': return 'Forget';
        }
    }

    confirm(): void {
        const device = this.target();
        if (!device) return;

        const decision = this.decision();
        const note = this.decisionNote();
        this.busy.set(true);
        this.dialogError.set(null);
        this.actionSub?.unsubscribe();

        const done = (message: string) => {
            this.busy.set(false);
            this.dialogOpen.set(false);
            this.note.set(message);
            this.reload();
        };
        const failed = (e: unknown) => {
            this.busy.set(false);
            this.dialogError.set(this.describe(e, 'edges:manage'));
        };

        if (decision === 'approve') {
            // The Edge being replaced travels with the approval, and it is the id the operator was just shown —
            // never a blanket yes. The API refuses anything else, which is what keeps a collision that appeared
            // since this dialog opened from being confirmed by a click aimed at a different machine.
            const replacing = this.collision();
            this.actionSub = this.api.approveEdgeDevice(device.requestId, note, replacing?.edgeId ?? null).subscribe({
                next: (updated) => done(
                    replacing
                        // The re-key happens at BINDING, which is this same request when the device already
                        // holds a key and is only waiting for consent, and its next contact otherwise. The
                        // answer says which of the two happened, so nobody goes and reinstalls a till that is
                        // in fact still trading.
                        ? `${updated.machineName} replaces ${replacing.machineName} at ${replacing.locationId}. `
                          + (updated.bound
                              ? `That machine's key no longer works.`
                              : `That machine's key stops working when ${updated.machineName} next reports in.`)
                        : updated.locationOnboarded
                            ? `Approved ${updated.machineName}. It takes its key on its next contact.`
                            : `Approved ${updated.machineName}. It takes its key on its next contact, and binds `
                              + `when ${updated.claimedLocationId} is onboarded.`),
                error: (e) => {
                    // A collision the list had not seen yet. Keep the dialog open, show the machine that would
                    // be replaced, and let the operator decide again against THAT one.
                    if (e?.error?.collision) this.collision.set(e.error.collision as EdgeDeviceCollision);
                    failed(e);
                }
            });
            return;
        }

        if (decision === 'reject') {
            this.actionSub = this.api.rejectEdgeDevice(device.requestId, note).subscribe({
                next: (updated) => done(`Rejected ${updated.machineName}.`),
                error: failed
            });
            return;
        }

        this.actionSub = this.api.forgetEdgeDevice(device.requestId).subscribe({
            next: () => done(`Removed ${device.machineName}. It enrols again on its own and will reappear here for approval.`),
            error: failed
        });
    }

    private describe(e: any, permission: string): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.error?.error) return e.error.error;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return `Forbidden — your role lacks the ${permission} permission.`;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
